import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useGameContext } from './context/GameContext';
import { GAME_GRADIENTS, GAME_GLOW } from './theme/theme';

// ─── Shared presentation helpers for home sections ────────────────────────────

export { GAME_GRADIENTS, GAME_GLOW };
export const RANK_MEDAL = { 1: '🥇', 2: '🥈', 3: '🥉' };

export function timeAgo(ts) {
  if (!ts) return '';
  const ms = ts.toMillis ? ts.toMillis() : (typeof ts === 'number' ? ts : Date.now());
  const seconds = Math.floor((Date.now() - ms) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export function nameColor(name) {
  const hue = [...(name || 'x')].reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
  return `hsl(${hue}, 70%, 60%)`;
}

// Translucent tint of any colour (hex or CSS variable).
export const mix = (color, percent) => `color-mix(in srgb, ${color} ${percent}%, transparent)`;

export function SectionTitle({ icon, label, count, children }) {
  return (
    <Box display="flex" alignItems="center" gap={1} mb={1.2}>
      <Typography sx={{ fontSize: '0.88rem' }}>{icon}</Typography>
      <Typography sx={{ fontWeight: 900, fontSize: '0.72rem', color: 'var(--app-text-soft)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </Typography>
      {count != null && (
        <Chip label={count} size="small" sx={{
          height: 16, minWidth: 22, fontSize: '0.6rem', fontWeight: 900,
          bgcolor: mix('var(--app-primary)', 14), color: 'var(--app-primary)',
          border: `1px solid ${mix('var(--app-primary)', 28)}`,
        }} />
      )}
      <Box sx={{ flex: 1 }} />
      {children}
    </Box>
  );
}

// ─── Guest gate ───────────────────────────────────────────────────────────────

function GuestGateMessage({ feature }) {
  const { loginWithGoogle } = useGameContext();
  return (
    <Box sx={{
      textAlign: 'center', py: 7, px: 3, borderRadius: '22px',
      border: '1px dashed var(--app-border-hover)',
      background: 'rgba(14,21,32,0.6)',
    }}>
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 2.5, repeat: Infinity }}
      >
        <Typography sx={{ fontSize: '2.8rem', mb: 1.5 }}>🔒</Typography>
      </motion.div>
      <Typography sx={{ color: 'var(--app-text-primary)', fontWeight: 900, fontSize: '1rem', mb: 0.6 }}>
        {feature} requires a Google account
      </Typography>
      <Typography sx={{ color: 'var(--app-text-muted)', fontSize: '0.78rem', mb: 3, lineHeight: 1.5 }}>
        Guest accounts can't access {feature.toLowerCase()}.<br />Sign in with Google to unlock it.
      </Typography>
      <Button
        variant="contained"
        onClick={loginWithGoogle}
        startIcon={
          <svg width="18" height="18" viewBox="0 0 48 48" style={{ display: 'block' }}>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.35-8.16 2.35-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
        }
        sx={{
          bgcolor: 'white', color: '#1f1f1f', borderRadius: '14px',
          fontWeight: 800, fontSize: '0.9rem', textTransform: 'none',
          px: 3, py: 1.2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          '&:hover': { bgcolor: '#f0f0f0', transform: 'translateY(-1px)', boxShadow: '0 6px 24px rgba(0,0,0,0.5)' },
          transition: 'all 0.2s',
        }}
      >
        Continue with Google
      </Button>
    </Box>
  );
}

export { GuestGateMessage };
